import { geolocation } from '@vercel/edge';
import cookie from 'cookie';
import type { IncomingMessage } from 'http';
import type { NextRequest, NextResponse } from 'next/server';

import { getPersistedCookieOptions } from '@/lib/experiments/experimentCookie';
import { isNonProductionEnv } from '@/lib/experiments/isProductionEnv';
import { StorageHelper } from '@/lib/storage';

export const GEO_COUNTRY_COOKIE = 'geo_country';
export const GEO_COUNTRY_HEADER = 'x-geo-country';
export const GEO_COUNTRY_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
export const GEO_COUNTRY_API_URL = '/api/geo-country/';

const INVALID_CF_COUNTRY_CODES = new Set(['XX', 'T1']);

function normalizeCountryCode(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toUpperCase();
  return normalized || undefined;
}

function isValidCountryCode(value: string | undefined): boolean {
  if (!value) return false;
  return /^[A-Z]{2}$/.test(value);
}

function readGeoCountryOverride(request: NextRequest): string | undefined {
  if (!isNonProductionEnv()) return undefined;

  const override = request.nextUrl.searchParams.get('geo_country');
  return normalizeCountryCode(override ?? undefined);
}

function parseCookieHeader(
  cookieHeader: string | undefined,
): Record<string, string> {
  if (!cookieHeader) return {};

  return cookieHeader.split(';').reduce<Record<string, string>>((acc, part) => {
    const [rawName, ...valueParts] = part.trim().split('=');
    if (!rawName) return acc;
    acc[rawName] = safeDecodeURIComponent(valueParts.join('='));
    return acc;
  }, {});
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readGeoCountryFromReqHeaders(
  headers: IncomingMessage['headers'] | undefined,
): string | undefined {
  const header = headers?.[GEO_COUNTRY_HEADER];
  const fromHeader = normalizeCountryCode(
    Array.isArray(header) ? header[0] : header,
  );
  if (fromHeader && isValidCountryCode(fromHeader)) {
    return fromHeader;
  }

  const cfHeader = headers?.['cf-ipcountry'];
  const fromCf = normalizeCountryCode(
    Array.isArray(cfHeader) ? cfHeader[0] : cfHeader,
  );
  if (fromCf && !INVALID_CF_COUNTRY_CODES.has(fromCf)) {
    return fromCf;
  }

  return undefined;
}

export function resolveGeoCountryFromReq(
  req: IncomingMessage | undefined,
  urlSearch = '',
): string | undefined {
  if (!req) return undefined;

  if (isNonProductionEnv() && urlSearch) {
    const params = new URLSearchParams(
      urlSearch.startsWith('?') ? urlSearch.slice(1) : urlSearch,
    );
    const override = normalizeCountryCode(
      params.get('geo_country') ?? undefined,
    );
    if (override && isValidCountryCode(override)) {
      return override;
    }
  }

  const fromHeaders = readGeoCountryFromReqHeaders(req.headers);
  if (fromHeaders) {
    return fromHeaders;
  }

  if (isNonProductionEnv()) {
    const devGeoCountry = normalizeCountryCode(process.env.DEV_GEO_COUNTRY);
    if (devGeoCountry && isValidCountryCode(devGeoCountry)) {
      return devGeoCountry;
    }
  }

  const cookies = parseCookieHeader(req.headers?.cookie);
  const fromCookie = normalizeCountryCode(cookies[GEO_COUNTRY_COOKIE]);
  return isValidCountryCode(fromCookie) ? fromCookie : undefined;
}

export function resolveGeoCountry(request: NextRequest): string | undefined {
  const override = readGeoCountryOverride(request);
  if (override && isValidCountryCode(override)) {
    return override;
  }

  const fromCf = normalizeCountryCode(
    request.headers.get('cf-ipcountry') ?? undefined,
  );
  if (fromCf && !INVALID_CF_COUNTRY_CODES.has(fromCf)) {
    return fromCf;
  }

  const fromEdge = normalizeCountryCode(
    geolocation(request)?.country ?? undefined,
  );
  if (fromEdge) {
    return fromEdge;
  }

  if (isNonProductionEnv()) {
    const devGeoCountry = normalizeCountryCode(process.env.DEV_GEO_COUNTRY);
    if (devGeoCountry && isValidCountryCode(devGeoCountry)) {
      return devGeoCountry;
    }
  }

  const fromCookie = normalizeCountryCode(
    request.cookies.get(GEO_COUNTRY_COOKIE)?.value ?? undefined,
  );
  return isValidCountryCode(fromCookie) ? fromCookie : undefined;
}

export function setGeoCountryCookie(
  geoCountry: string | undefined,
  request: NextRequest,
  response: NextResponse,
): void {
  if (!geoCountry || !isValidCountryCode(geoCountry)) return;

  const existing = normalizeCountryCode(
    request.cookies.get(GEO_COUNTRY_COOKIE)?.value ?? undefined,
  );
  if (existing === geoCountry) return;

  response.cookies.set(GEO_COUNTRY_COOKIE, geoCountry, {
    ...getPersistedCookieOptions(GEO_COUNTRY_COOKIE_MAX_AGE_SECONDS),
  });
}

export function applyGeoCountry(
  request: NextRequest,
  response: NextResponse,
  requestHeaders: Headers,
  geoCountry?: string,
): string | undefined {
  const resolved = geoCountry ?? resolveGeoCountry(request);

  if (resolved) {
    requestHeaders.set(GEO_COUNTRY_HEADER, resolved);
  }

  setGeoCountryCookie(resolved, request, response);
  return resolved;
}

export function getCountryIP(): string {
  if (typeof document === 'undefined') return '';

  const match = /(?:^|;\s*)geo_country=([^;]*)/.exec(document.cookie);
  return match?.[1]?.toUpperCase() ?? '';
}

export function readGeoCountryFromClientCookie(): string | undefined {
  const country = getCountryIP();
  return isValidCountryCode(country) ? country : undefined;
}

export function writeGeoCountryCookieHeader(
  res: { setHeader(name: string, value: string | string[]): void },
  geoCountry: string,
  existingGeoCountry?: string,
): void {
  if (!isValidCountryCode(geoCountry)) return;
  if (normalizeCountryCode(existingGeoCountry) === geoCountry) return;

  const options = getPersistedCookieOptions(GEO_COUNTRY_COOKIE_MAX_AGE_SECONDS);
  res.setHeader(
    'Set-Cookie',
    cookie.serialize(GEO_COUNTRY_COOKIE, geoCountry, {
      maxAge: options.maxAge,
      path: options.path,
      secure: options.secure,
      sameSite: options.sameSite,
    }),
  );
}

export async function syncGeoCountryCookie(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;

  const existing = readGeoCountryFromClientCookie();
  if (existing) return existing;

  try {
    const response = await fetch(GEO_COUNTRY_API_URL, {
      credentials: 'same-origin',
    });
    if (!response.ok) return undefined;

    const payload = (await response.json()) as { country?: string | null };
    const country = normalizeCountryCode(payload.country ?? undefined);
    if (!country || !isValidCountryCode(country)) return undefined;

    const storageHelper = new StorageHelper();
    const cookieOptions = getPersistedCookieOptions(
      GEO_COUNTRY_COOKIE_MAX_AGE_SECONDS,
    );
    storageHelper.setCookie(GEO_COUNTRY_COOKIE, country, {
      expires: GEO_COUNTRY_COOKIE_MAX_AGE_SECONDS / 86400,
      secure: cookieOptions.secure,
      sameSite: cookieOptions.sameSite,
    });

    return country;
  } catch {
    return undefined;
  }
}
